import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { TextField as MaterialTextField, InputAdornment, CircularProgress } from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import classNames from "classnames";
import styles from "./field-dropdown.module.scss";
import locale from "localization";

const FieldWithDropdown = ({
                               name,
                               value = "",
                               placeholder = "",
                               error,
                               options = [],
                               onChange,
                               onBlur,
                               onFocus,
                               onInputChange,
                               disabled = false,
                               className,
                               upperCase = false,
                               loading = false,
                               renderOption,
                               getOptionLabel,
                               getOptionSearchText,
                           }) => {
    const [searchValue, setSearchValue] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState("");
    const [selectedOption, setSelectedOption] = useState(null);
    const inputRef = useRef();
    const optionsRef = useRef();

    const filteredOptions = useMemo(() => {
        const q = searchValue?.trim();
        if (!q) {
            return options;
        }
        return options.filter((option) => {
            const searchText = getOptionSearchText ? getOptionSearchText(option) : option.label;
            return searchText.toLowerCase().includes(q.toLowerCase());
        });
    }, [searchValue, options, getOptionSearchText]);

    const displayValue = useMemo(() => {
        if (isOpen) {
            return searchValue;
        }

        let textToDisplay = "";
        let isShowingSearchValue = false;
        const currentOption = selectedOption || options.find((opt) => opt.value === value);
        const selectedOptionLabel = currentOption ? currentOption.label : "";

        if (searchValue && searchValue !== selectedOptionLabel && searchValue !== selectedLabel) {
            textToDisplay = searchValue;
            isShowingSearchValue = true;
        } else if (selectedLabel) {
            textToDisplay = selectedLabel;
        } else if (currentOption) {
            textToDisplay = currentOption.label;
        }

        if (textToDisplay && textToDisplay.length > 42 && !isShowingSearchValue) {
            return textToDisplay.substring(0, 42) + "...";
        }

        return textToDisplay;
    }, [isOpen, searchValue, selectedLabel, selectedOption, value, options]);

    const handleInputChange = useCallback(
        (e) => {
            let inputValue = e.target.value;
            setSearchValue(inputValue);
            if (onInputChange) {
                onInputChange(inputValue);
            }
            if (!inputValue) {
                setSelectedLabel("");
                setSelectedOption(null);
                if (onChange) {
                    onChange(name, { value: "", label: "" });
                }
            }
        },
        [onChange, name, onInputChange]
    );

    const handleFocus = useCallback(
        (e) => {
            setIsOpen(true);
            if (onFocus) {
                onFocus(e);
            }
        },
        [onFocus]
    );

    const handleBlur = useCallback(
        (e) => {
            setTimeout(() => {
                setIsOpen(false);
                // If the text was cleared, ensure display is empty after blur
                if (!searchValue?.trim() && !value && !selectedLabel) {
                    setSearchValue("");
                }
                if (onBlur) {
                    onBlur(name, { value });
                }
            }, 200);
        },
        [onBlur, name, value, searchValue, selectedLabel]
    );

    const handleOptionSelect = useCallback(
        (option) => {
            const displayText = getOptionLabel ? getOptionLabel(option) : option.label;
            setSelectedLabel(displayText);
            setSearchValue(displayText);
            setSelectedOption(option);
            setIsOpen(false);
            if (inputRef.current) {
                const inputElement = inputRef.current.querySelector('input') || inputRef.current.querySelector('textarea');
                if (inputElement && inputElement.blur) {
                    inputElement.blur();
                }
            }
            if (onChange) {
                onChange(name, { value: option.value, label: option.label });
            }
        },
        [onChange, name, getOptionLabel]
    );

    const handleArrowClick = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const isOptionInactive = useCallback((opt) => {
        const status = (opt?.status || "").toString().toLowerCase();
        return opt?.inactive === true || status === "inactive";
    }, []);

    useEffect(() => {
        if (!value) {
            setSelectedOption(null);
            setSelectedLabel("");
            setSearchValue("");
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen &&
                inputRef.current &&
                optionsRef.current &&
                !inputRef.current.contains(event.target) &&
                !optionsRef.current.contains(event.target)) {
                setIsOpen(false);
                // If the text was cleared, ensure display is empty
                if (!searchValue?.trim() && !value && !selectedLabel) {
                    setSearchValue("");
                }
                if (onBlur) {
                    onBlur(name, { value });
                }
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isOpen, searchValue, value, selectedLabel, onBlur, name]);

    const showSelectedOverlay = !isOpen && selectedOption && renderOption;

    return (
        <div className={classNames(styles.container, className)}>
            {showSelectedOverlay && (
                <div
                    className={styles.selectedDisplay}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        if (inputRef.current) {
                            const inputElement = inputRef.current.querySelector('input') || inputRef.current.querySelector('textarea');
                            if (inputElement) inputElement.focus();
                        }
                    }}
                >
                    {renderOption(selectedOption)}
                </div>
            )}
            <MaterialTextField
                inputRef={inputRef}
                className={classNames(styles.input, {
                    [`${styles.disabled}`]: disabled,
                    [`${styles.active}`]: isOpen,
                })}
                error={error}
                value={displayValue}
                variant="outlined"
                placeholder={placeholder}
                disabled={disabled}
                multiline={isOpen}
                maxRows={isOpen ? 1 : 1}
                minRows={1}
                inputProps={{
                    maxLength: 150,
                    style: {
                        ...(isOpen ? { overflowY: 'auto', resize: 'none' } : {}),
                        ...(showSelectedOverlay ? { color: 'transparent' } : {}),
                    }
                }}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            {loading ? (
                                <CircularProgress size={18} thickness={4} style={{ color: "#34384b" }} />
                            ) : (
                                <ArrowDropDownIcon
                                    className={styles.arrowIcon}
                                    onClick={handleArrowClick}
                                />
                            )}
                        </InputAdornment>
                    ),
                }}
            />
            <div
                ref={optionsRef}
                className={classNames(styles.options, {
                    [`${styles.open}`]: isOpen,
                    [`${styles.empty}`]: !filteredOptions.length,
                })}
            >
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => {
                        const inactive = isOptionInactive(option);
                        return (
                            <div
                                key={option.value || index}
                                className={classNames(styles.option, {
                                    [`${styles.selected}`]: option.value === value,
                                })}
                                onMouseDown={() => {
                                    handleOptionSelect(option);
                                }}
                            >
                                <span className={styles.optionLabel}>{renderOption ? renderOption(option) : option.label}</span>
                                <span className={classNames(styles.inactiveLabel, {
                                    [styles.visible]: inactive
                                })}>{locale.inactive.toUpperCase()}</span>
                            </div>
                        );
                    })
                ) : (
                    <div className={styles.option}>
                        <span className={styles.noData}>No options found</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FieldWithDropdown;